import { ResponsiveStream } from "@nivo/stream";
import { Header, Paragraph } from "components/atoms/Typography";
import { theme } from "components/molecules/Charts/ChartTheme";
import { getColorMappingWithoutKeyForChart } from "constants/chartsColors.constant";
import { useMemo } from "react";
import { UseQueryResult } from "react-query";
import { CURRENT_YEAR } from "utils/constants";
import { getDisplayInfo } from "utils/formatting";
import "./AnnualEmissionSavingsByMeasureChart.css";

interface AnnualEmissionSavingsByMeasureChartProps {
    query: () => UseQueryResult<AnnualEmissionSavingsByMeasureChartData | null>;
    backgroundColor?: string;
    legendColumns?: number;
    legendTitle?: string;
}

function AnnualEmissionSavingsByMeasureChart({
    query,
    backgroundColor = "white",
    legendColumns = 1,
    legendTitle = "Measure types by impact",
}: AnnualEmissionSavingsByMeasureChartProps) {
    const { data } = query();

    const streamChartData = useMemo(() => {
        if (!data) return null;
        const uniqueKeys = new Set<string>();
        data.forEach((datum) => {
            datum?.carbonReductionMeasureCategories.forEach((category) => {
                if (category?.carbonReductionMeasureCategory) uniqueKeys.add(category?.carbonReductionMeasureCategory);
            });
        });

        const desiredOrder = [
            "Carbon emissions",
            "Heating/Cooling",
            "Heating",
            "Cooling",
            "Ventilation",
            "Domestic Hot Water",
            "Envelope",
            "Lighting",
            "Process",
            "Controls",
            "Conveying Equipment",
        ];

        // Get remaining categories
        const remainingKeys = Array.from(uniqueKeys).filter((key) => !desiredOrder.includes(key));

        let dataMax = 0;
        const formatted = data.map((datum) => {
            let measureTotal = 0;
            const measuresToCarbonEmissions: { [measure: string]: number } = {};
            datum.carbonReductionMeasureCategories.forEach((category) => {
                if (category.carbonReductionMeasureCategory) {
                    measuresToCarbonEmissions[category.carbonReductionMeasureCategory] =
                        category.totalCarbonEmissionSavings;
                    measureTotal += category.totalCarbonEmissionSavings;
                }
            });

            const arr = Array.from(uniqueKeys).reduce(
                (prev, curr) => ({
                    ...prev,
                    [curr]: 0,
                }),
                {}
            );

            const gridSavings = datum.carbonEmissionSavingsFromGrid;
            measureTotal += datum.carbonEmissionSavingsFromGrid;
            measureTotal += datum.remainingCarbonEmissions;

            if (measureTotal > dataMax) dataMax = measureTotal;

            return {
                year: datum.year,
                "Grid savings": gridSavings,
                ...arr,
                ...measuresToCarbonEmissions,
                "Carbon emissions": datum.remainingCarbonEmissions,
            };
        });

        return {
            formatted,
            max: dataMax,
            chartKeys: [
                ...desiredOrder.filter((key) => key === "Carbon emissions" || uniqueKeys.has(key)),
                ...remainingKeys,
                "Grid savings",
            ],
        };
    }, [data]);

    const { unit, formatFunction } = useMemo(
        () => getDisplayInfo("carbonEmissions", streamChartData?.max || 0),
        [streamChartData]
    );

    const chartColors = useMemo(() => {
        if (!streamChartData || streamChartData?.formatted?.length === 0) return [];

        const colors = getColorMappingWithoutKeyForChart(streamChartData.chartKeys);
        return colors;
    }, [streamChartData]);

    const margin = {
        top: 20,
        right: 26,
        bottom: 25,
        left: 95,
    };

    const gradientDefs = useMemo(() => {
        if (!streamChartData?.chartKeys) return [];

        return streamChartData.chartKeys.map((key, index) => ({
            id: `gradient-${index}`,
            type: "linearGradient",
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
            colors: [
                { offset: 0, color: chartColors[index] || "#000", opacity: 1 },
                { offset: 65, color: chartColors[index] || "#000", opacity: 1 },
                {
                    offset: 100,
                    color: chartColors[index] || "#000",
                    opacity: 0.8,
                },
            ],
        }));
    }, [streamChartData?.chartKeys, chartColors]);

    const fillPatterns = useMemo(() => {
        if (!streamChartData?.chartKeys) return [];

        return streamChartData.chartKeys.map((key, index) => ({
            match: { id: key },
            id: `gradient-${index}`,
        }));
    }, [streamChartData?.chartKeys]);

    return (
        <div className="stream-chart-container" style={legendColumns > 1 ? { flexDirection: "column" } : undefined}>
            <div
                className="stream-chart"
                style={{
                    height: "520px",
                    ...(legendColumns > 1 ? { flex: "none" } : {}),
                }}
            >
                <ResponsiveStream
                    data={
                        (streamChartData?.formatted as {
                            [key: string]: number;
                        }[]) || []
                    }
                    defs={gradientDefs}
                    fill={fillPatterns}
                    theme={{
                        ...theme,
                        background: backgroundColor,
                    }}
                    keys={streamChartData?.chartKeys || []}
                    valueFormat={(v) => formatFunction(v, false)}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                        tickSize: 3,
                        tickPadding: 5,
                        legendPosition: "middle",
                        legendOffset: 36,
                        format: (v) => {
                            if (v === 0) return CURRENT_YEAR;
                            if (v === (streamChartData?.formatted.length || 0) - 1) return "2050";
                            return "";
                        },
                    }}
                    axisLeft={{
                        tickSize: 3,
                        tickPadding: 5,
                        tickRotation: 0,
                        legendPosition: "middle",
                        legend: unit,
                        legendOffset: margin ? margin.left * -1 + 5 : -50,
                        format: (v) => formatFunction(v, false),
                    }}
                    colors={chartColors}
                    offsetType="none"
                    margin={margin}
                    enableStackTooltip={false}
                />
            </div>
            <AnnualEmissionSavingsByMeasureLegend
                measureCategories={streamChartData?.chartKeys || []}
                colors={chartColors.slice(1, chartColors.length - 1)}
                columns={legendColumns}
                title={legendTitle}
            />
        </div>
    );
}

interface AnnualEmissionSavingsByMeasureLegendProps {
    measureCategories: string[];
    colors: string[];
    columns?: number;
    title?: string;
}

function AnnualEmissionSavingsByMeasureLegend({
    measureCategories,
    colors,
    columns = 1,
    title = "Measure types by impact",
}: AnnualEmissionSavingsByMeasureLegendProps) {
    const measures = measureCategories.filter((name) => name !== "Grid savings" && name !== "Carbon emissions");

    const gridStyle =
        columns > 1 ? { display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: "4px 16px" } : undefined;

    return (
        <div className="annual-emission-savings-by-measure-legend">
            <Header style={{ color: "var(--audette-gray-600)" }} size="x-small" weight="medium">
                {title}
            </Header>
            <div style={gridStyle}>
                {measures.map((name, index) => (
                    <div className="row" key={colors[index]}>
                        <LegendColor color={colors[index]} />
                        <Paragraph size="small" style={{ color: "var(--audette-gray-600)" }}>
                            {name}
                        </Paragraph>
                    </div>
                ))}
                <div className="row">
                    <LegendColor color="var(--audette-gray-300)" />
                    <Paragraph size="small" style={{ color: "var(--audette-gray-600)" }}>
                        Grid savings
                    </Paragraph>
                </div>
                <div className="row">
                    <LegendColor color="var(--audette-gray-600)" />
                    <Paragraph size="small" style={{ color: "var(--audette-gray-600)" }}>
                        Carbon emissions
                    </Paragraph>
                </div>
            </div>
        </div>
    );
}

function LegendColor({ color }: { color: string }) {
    return (
        <div
            style={{
                backgroundColor: color,
                width: "16px",
                height: "16px",
                borderRadius: "4px",
            }}
        />
    );
}

export default AnnualEmissionSavingsByMeasureChart;
